import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Fingerprint } from "lucide-react";
import Confetti from "react-confetti";
import { Fireworks } from "@fireworks-js/react";

const InaugurationAnimation = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [started, setStarted] = useState(false);
  const [curtainOpen, setCurtainOpen] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 1280, height: 720 });
  const [currentTime, setCurrentTime] = useState(new Date());

  const line1 = "بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ";
  const line2 = <img src="/assets/img/logo/new-logo.png" alt="Logo" className="logo" />;
  const line4 = "New OPD Reception";
  const line5 = "Inaugurated By";
  const line6 = "Mr. Hanif ssAbbasi";
  const line7 = "Federal Minister of Railways";
  const line8 = "on 13th August, 2025";

  const [typedLines, setTypedLines] = useState(["", null, "", "", "", "", "", ""]);

  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    onResize();
    window.addEventListener("resize", onResize);
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      window.removeEventListener("resize", onResize);
      clearInterval(t);
    };
  }, []);

  const formatTime = (d) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const formatDate = (d) =>
    d.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  const handleStart = () => {
    if (isScanning || started) return;
    setIsScanning(true);

    setTimeout(() => {
      setIsScanning(false);
      setStarted(true);
      setTimeout(() => setCurtainOpen(true), 60);
      setTimeout(() => setShowCelebration(true), 1200);
    }, 2000); // longer scanning animation
  };

  useEffect(() => {
    if (!showCelebration) return;
    let cancelled = false;

    const typeLine = async (text, index, instant = false) => {
      if (cancelled) return;
      if (instant) {
        setTypedLines((p) => {
          const c = [...p];
          c[index] = text;
          return c;
        });
        return;
      }
      setTypedLines((p) => {
        const c = [...p];
        c[index] = "";
        return c;
      });
      for (let i = 0; i < text.length; i++) {
        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 60));
        setTypedLines((p) => {
          const c = [...p];
          c[index] = c[index] + text.charAt(i);
          return c;
        });
      }
    };

    (async () => {
      await typeLine(line1, 0);
      await typeLine(line2, 1, true);
      await typeLine(line4, 2);
      await typeLine(line5, 3);
      await typeLine(line6, 4);
      await typeLine(line7, 5);
      await typeLine(line8, 6);
    })();

    return () => {
      cancelled = true;
    };
  }, [showCelebration]);

  return (
    <>
      <style>{`
        .inauguration-container {
          position: relative;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          font-family: 'Segoe UI', sans-serif;
          background: ${started && curtainOpen ? "white" : "url('/assets/img/building.jpg') center/cover no-repeat"};
          transition: background 1s ease;
        }
        .inauguration-container::before {
          content: '';
          position: absolute;
          inset: 0;
          background: ${started && curtainOpen ? "transparent" : "rgba(0,0,0,0.55)"};
          backdrop-filter: ${started && curtainOpen ? "none" : "blur(3px)"};
          z-index: 1;
          transition: all 1s ease;
        }
        .flag-bg {
          position: absolute;
          inset: 0;
          background: url('/assets/img/pakistan-flag.png') center/cover;
          opacity: 0.25;
          animation: waveFlag 8s ease-in-out infinite;
          z-index: -3;
        }
        @keyframes waveFlag {
          0%, 100% { transform: perspective(900px) rotateY(0deg) scale(1.05); }
          50% { transform: perspective(900px) rotateY(2deg) scale(1.05); }
        }
        .clock {
          position: absolute;
          top: 20px;
          right: 30px;
          color: white;
          font-size: 1.1rem;
          z-index: 50;
          text-align: right;
        }
        .button-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 20;
        }
        .logo {
          max-width: 140px;
          margin-bottom: 2rem;
          filter: drop-shadow(0 0 25px rgba(255,255,255,0.4));
        }
        .tagline {
          font-size: 1.6rem;
          font-weight: 600;
          color: #FFD700;
          margin-bottom: 2rem;
          text-shadow: 0 0 10px rgba(255,215,0,0.8);
        }
        .open-button {
          position: relative;
          padding: 1.2rem 2.6rem;
          font-size: 1.7rem;
          font-weight: 700;
          color: white;
          border: none;
          border-radius: 9999px;
          background: linear-gradient(135deg, #006400, #228B22, #FFD700);
          cursor: pointer;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 12px;
        }
        .fingerprint-icon {
          stroke: white;
          animation: ${isScanning ? "fingerScan 2s linear infinite" : "none"};
        }
        @keyframes fingerScan {
          0% { filter: drop-shadow(0 0 0 rgba(0,255,136,0)); transform: translateY(0); }
          50% { filter: drop-shadow(0 0 14px rgba(0,255,136,0.8)); transform: translateY(-2px); }
          100% { filter: drop-shadow(0 0 0 rgba(0,255,136,0)); transform: translateY(0); }
        }
        .scan-bar {
          position: absolute;
          left: 8%;
          right: 8%;
          height: 6px;
          bottom: -10px;
          border-radius: 6px;
          overflow: hidden;
          opacity: ${isScanning ? 1 : 0};
          transition: opacity 0.3s ease;
        }
        .scan-bar .bar {
          height: 100%;
          width: 18%;
          background: linear-gradient(90deg, rgba(255,255,255,0.95), rgba(0,255,136,0.95));
          animation: barMove 1.1s linear infinite;
        }
        @keyframes barMove {
          0% { transform: translateX(-12%); }
          100% { transform: translateX(112%); }
        }
        .curtain-wrap {
          position: absolute;
          inset: 0;
          z-index: 80;
          pointer-events: none;
        }
        .curtain-panel {
          position: absolute;
          top: 0;
          height: 100%;
          width: 52%;
          overflow: hidden;
          display: flex;
          align-items: stretch;
          transition: transform 1.2s cubic-bezier(.2,.9,.2,1);
        }
        .curtain-panel.left { left: 0; transform: translateX(0); }
        .curtain-panel.right { right: 0; transform: translateX(0); }
        .curtain-panel.open.left { transform: translateX(-82%); }
        .curtain-panel.open.right { transform: translateX(82%); }
        .fabric {
          flex: 1;
          background: #0b6623;
          background-image:
            linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%),
            repeating-linear-gradient(90deg, rgba(0,0,0,0.08) 0 2px, rgba(255,255,255,0.05) 2px 24px);
          animation: fabricWave 2.6s ease-in-out infinite;
        }
        @keyframes fabricWave {
          0%, 100% { transform: skewY(0deg) translateY(0); }
          50% { transform: skewY(1.2deg) translateY(2px); }
        }
        .video-overlay-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .event-line {
          font-size: clamp(22px, 3.6vw, 44px);
          font-weight: 700;
          color: #0b6623;
        }
        .guest-line {
          font-size: clamp(26px, 4.2vw, 56px);
          font-weight: 800;
          color: #FFD700;
        }
      `}</style>

      <div className="inauguration-container">
        <div className="clock">
          {formatDate(currentTime)} <br /> {formatTime(currentTime)}
        </div>

        <div className="flag-bg" />

        {!started && (
          <div className="button-overlay">
            <img src="../assets/img/logo/new-logo.png" className="logo" alt="Logo" />
            <div className="tagline">Ribbon Cutting Ceremony</div>
            <motion.button
              className="open-button"
              onClick={handleStart}
              onTouchStart={handleStart}
              whileTap={{ scale: 0.96 }}
            >
              <Fingerprint className="fingerprint-icon" size={36} strokeWidth={1.5} />
              <div style={{ fontSize: 16, fontWeight: 700 }}>Touch to Open</div>
              <div className="scan-bar">
                <div className="bar" />
              </div>
            </motion.button>
          </div>
        )}

        {started && (
          <div className="curtain-wrap">
            <div className={`curtain-panel left ${curtainOpen ? "open" : ""}`}>
              <div className="fabric" />
            </div>
            <div className={`curtain-panel right ${curtainOpen ? "open" : ""}`}>
              <div className="fabric" />
            </div>
          </div>
        )}

        {showCelebration && (
          <>
            <Confetti width={windowSize.width} height={windowSize.height} />
            <Fireworks
              options={{
                speed: 3,
                particles: 100,
                trace: 3,
                explosion: 5,
                autoresize: true,
                brightness: { min: 50, max: 85 },
              }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 190,
              }}
            />
            <motion.div
              className="video-overlay-text"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2 }}
            >
              <div className="event-line">{typedLines[0]}</div>
              <div className="event-line">{typedLines[1]}</div>
              <div className="event-line">{typedLines[2]}</div>
              <div className="event-line">{typedLines[3]}</div>
              <div className="guest-line">{typedLines[4]}</div>
              <div className="event-line">{typedLines[5]}</div>
              <div className="event-line">{typedLines[6]}</div>
            </motion.div>
          </>
        )}
      </div>
    </>
  );
};

export default InaugurationAnimation;
